<app-legal-page-layout
  kicker="Legal"
  title="Age Verification & Restricted Access Policy"
  description="Safeword is an adult-only platform intended exclusively for individuals who are 18 years of age or older."
  [metaLines]="['Operated by Hove Retail Limited']"
  [sections]="sections">

  <p>
    The Services are designed for adults and must not be accessed, used or registered for by anyone under the age
    of 18.
  </p>
  <p>
    Hove Retail Limited is committed to protecting children from exposure to adult content and takes reasonable
    steps to prevent underage individuals from accessing the Safeword platform.
  </p>

  <section id="age-restriction">
    <h2>Age Restriction</h2>
    <p>Access to Safeword is strictly limited to individuals who are at least 18 years of age.</p>
    <p>By creating an account, using the Services or uploading content, you confirm that:</p>
    <ul>
      <li>you are at least 18 years of age;</li>
      <li>the information you provide is accurate;</li>
      <li>you are legally entitled to access adult content in your jurisdiction; and</li>
      <li>you will comply with our Terms of Service, Community Standards and applicable law.</li>
    </ul>
    <p>
      Providing false or misleading information about your age may result in immediate suspension or permanent
      termination of your account.
    </p>
  </section>

  <section id="age-verification">
    <h2>Age Verification</h2>
    <p>
      To help maintain Safeword as an adult-only platform, we may require users to complete age verification
      through <strong>VerifyMyAge</strong> or another approved age verification provider.
    </p>
    <p>Age verification may be required:</p>
    <ul>
      <li>when creating an account;</li>
      <li>before accessing certain features;</li>
      <li>before viewing adult content;</li>
      <li>before purchasing or selling content;</li>
      <li>before becoming a verified creator; or</li>
      <li>where we reasonably believe additional verification is necessary.</li>
    </ul>
    <p>
      Where verification cannot be successfully completed, access to some or all of the Services may be refused,
      restricted or withdrawn.
    </p>
  </section>

  <section id="underage-user">
    <h2>If We Become Aware of an Underage User</h2>
    <p>
      If Safeword becomes aware, or has reasonable grounds to believe, that an account belongs to an individual
      under the age of 18, we may take immediate action, including:
    </p>
    <ul>
      <li>suspending or restricting the account;</li>
      <li>preventing further access to the Services;</li>
      <li>removing or restricting access to uploaded content;</li>
      <li>preserving relevant information where required by law;</li>
      <li>investigating the circumstances surrounding the account; and</li>
      <li>permanently deleting the account where appropriate.</li>
    </ul>
    <p>Our priority is to protect children, safeguard the community and comply with applicable legal obligations.</p>
  </section>

  <section id="underage-content">
    <h2>Removal of Underage Content</h2>
    <p>
      Any content that appears to depict, involve or have been uploaded by an individual under the age of 18 will
      be treated as an extremely serious matter.
    </p>
    <p>Where appropriate, Safeword may:</p>
    <ul>
      <li>immediately restrict access to the content;</li>
      <li>remove the content from public view;</li>
      <li>preserve relevant evidence where legally required;</li>
      <li>suspend or terminate associated accounts;</li>
      <li>investigate related activity;</li>
      <li>cooperate with law enforcement or other competent authorities where required or permitted by law; and</li>
      <li>take any other action reasonably necessary to protect individuals and comply with applicable legislation.</li>
    </ul>
  </section>

  <section id="reporting-concerns">
    <h2>Reporting Concerns</h2>
    <p>
      Members who believe that an account or content may involve an individual under the age of 18 are encouraged
      to report their concerns immediately using Safeword's reporting tools or by contacting our support team.
    </p>
    <p>Reports relating to the safety of children are prioritised and investigated as quickly as reasonably practicable.</p>
    <p>Where appropriate, additional information may be requested to assist our investigation.</p>
  </section>

  <section id="creator-responsibilities">
    <h2>Creator Responsibilities</h2>
    <p>
      Creators have a continuing responsibility to ensure that all individuals appearing in content uploaded to
      Safeword:
    </p>
    <ul>
      <li>are at least 18 years of age;</li>
      <li>have freely and knowingly consented to the creation of the content;</li>
      <li>have freely and knowingly consented to its publication on Safeword; and</li>
      <li>comply with all applicable laws and our platform policies.</li>
    </ul>
    <p>
      Where reasonably required for compliance or the investigation of complaints, creators may be asked to provide
      evidence supporting these declarations.
    </p>
    <p>
      Failure to cooperate with legitimate compliance requests may result in enforcement action, including
      suspension or termination of creator privileges.
    </p>
  </section>

  <section id="safeguarding-compliance">
    <h2>Safeguarding and Compliance</h2>
    <p>
      Safeword is committed to maintaining effective safeguarding measures designed to reduce the risk of children
      accessing adult content.
    </p>
    <p>Our approach may include:</p>
    <ul>
      <li>age verification procedures;</li>
      <li>AI-assisted detection technologies;</li>
      <li>trained human moderators;</li>
      <li>member reporting tools;</li>
      <li>proactive trust and safety measures;</li>
      <li>fraud prevention systems; and</li>
      <li>ongoing review of our safeguarding processes.</li>
    </ul>
    <p>
      These measures are regularly reviewed and updated to reflect developments in technology, industry best
      practice and applicable legal requirements.
    </p>
  </section>

  <section id="cooperation-authorities">
    <h2>Cooperation with Authorities</h2>
    <p>
      Where required or permitted by law, Safeword may cooperate with law enforcement agencies, safeguarding
      organisations, regulators or other competent authorities in relation to suspected offences involving children
      or other safeguarding concerns.
    </p>
    <p>
      Such cooperation may include preserving relevant information, responding to lawful requests for information
      and taking immediate action to prevent further harm.
    </p>
  </section>

  <section id="our-commitment">
    <h2>Our Commitment</h2>
    <p>Protecting children is one of Safeword's highest priorities.</p>
    <p>
      We are committed to operating an adult-only platform that promotes lawful adult expression while taking
      appropriate steps to prevent underage access, respond promptly to safeguarding concerns and comply with our
      legal obligations under applicable UK legislation.
    </p>
    <p>
      Members who become aware of any activity that may place a child at risk are encouraged to report it
      immediately so that appropriate action can be taken.
    </p>
  </section>

</app-legal-page-layout>
